import React from 'react';

import {
	AiPanelBiohackStrategy,
	AiPanelBiomarker,
	AiPanelCognitiveStrengthsOpportunities,
	AiPanelDietRecommendation,
	AiPanelExerciseRecommendation,
	AiPanelFurtherReading,
	AiPanelInjuryRisk,
	AiPanelLifestyleRecommendation,
	AiPanelPerformanceOptimization,
	AiPanelPersonalityDimensions,
	AiPanelPharmaceuticalInteraction,
	AiPanelPowerVsEndurance,
	AiPanelRecoveryProfile,
	AiPanelScreeningRecommendation,
	AiPanelSpecificAdvantage,
	AiPanelThingToAvoid,
} from '../../../../../types/geneCodes/AiPanelReportTypes';
import { RecordList, RecordSpec, SeverityTag } from './RecordList';
import { BulletList, Html, RecordCard, SubHeading, hasItems, tags, text } from './primitives';
import './panelReport.css';

// Every spec below is `title`, an optional badge field, and the ordered label/value pairs
// that used to be a hand-written `rows={[...]}` in its own file.

export const AVOID: RecordSpec<AiPanelThingToAvoid> = {
	title: (e) => text(e.item),
	tag: (e) => text(e.severity),
	fields: [
		['Category', (e) => text(e.category)],
		['Rationale', (e) => text(e.rationale)],
		['Related genes', (e) => e.related_genes, 'tags'],
		['Alternatives', (e) => e.alternatives, 'tags'],
	],
};

export const DIET: RecordSpec<AiPanelDietRecommendation> = {
	title: (e) => text(e.recommendation),
	tag: (e) => text(e.priority),
	fields: [
		['Rationale', (e) => text(e.rationale)],
		['Related genes', (e) => e.related_genes, 'tags'],
		['Related traits', (e) => e.related_traits, 'tags'],
		['Specific foods', (e) => e.specific_foods, 'tags'],
		['Supplements', (e) => e.supplements, 'tags'],
	],
};

export const EXERCISE: RecordSpec<AiPanelExerciseRecommendation> = {
	title: (e) => text(e.recommendation),
	tag: (e) => text(e.intensity),
	fields: [
		['Rationale', (e) => text(e.rationale)],
		['Exercise type', (e) => text(e.exercise_type)],
		['Frequency', (e) => text(e.frequency)],
		['Duration', (e) => text(e.duration)],
		['BMI range', (e) => text(e.bmi_range)],
		['Related genes', (e) => e.related_genes, 'tags'],
		['Contraindications', (e) => e.contraindications, 'tags'],
	],
};

export const LIFESTYLE: RecordSpec<AiPanelLifestyleRecommendation> = {
	title: (e) => text(e.recommendation),
	tag: (e) => text(e.priority),
	fields: [
		['Rationale', (e) => text(e.rationale)],
		['Category', (e) => text(e.category)],
		['Related genes', (e) => e.related_genes, 'tags'],
		['Implementation tips', (e) => e.implementation_tips, 'tags'],
	],
};

export const PHARMACEUTICAL: RecordSpec<AiPanelPharmaceuticalInteraction> = {
	title: (e) => text(e.drug_name),
	fields: [
		['Gene involved', (e) => text(e.gene_involved)],
		['Interaction type', (e) => text(e.interaction_type)],
		['Mechanism', (e) => text(e.mechanism)],
		['Clinical impact', (e) => text(e.clinical_impact)],
		['Recommendation', (e) => text(e.recommendation)],
		['Monitoring required', (e) => text(e.monitoring_required)],
	],
};

export const BIOHACK: RecordSpec<AiPanelBiohackStrategy> = {
	title: (e) => text(e.intervention),
	tag: (e) => text(e.evidence_level),
	fields: [
		['Type', (e) => text(e.type)],
		['Mechanism', (e) => text(e.mechanism)],
		['Target genes', (e) => e.target_genes, 'tags'],
		['Expected benefits', (e) => text(e.expected_benefits)],
		['Risks', (e) => text(e.risks)],
	],
};

export const ADVANTAGE: RecordSpec<AiPanelSpecificAdvantage> = {
	title: (e) => text(e.trait),
	fields: [
		['Genes involved', (e) => e.genes_involved, 'tags'],
		['Description', (e) => text(e.description)],
	],
};

export const BIOMARKER: RecordSpec<AiPanelBiomarker> = {
	title: (e) => text(e.name),
	fields: [
		['Ideal range', (e) => text(e.ideal_range)],
		['Critical values', (e) => text(e.critical_values)],
		['Testing frequency', (e) => text(e.testing_frequency)],
		['Rationale', (e) => text(e.rationale)],
		['Related conditions', (e) => e.related_conditions, 'tags'],
	],
};

export const SCREENING: RecordSpec<AiPanelScreeningRecommendation> = {
	title: (e) => text(e.test),
	fields: [
		['Age to start', (e) => text(e.age_to_start)],
		['Frequency', (e) => text(e.frequency)],
		['Rationale', (e) => text(e.rationale)],
	],
};

export const FURTHER_READING: RecordSpec<AiPanelFurtherReading> = {
	title: (e) => text(e.title),
	fields: [
		['Author', (e) => {
			const byline = [text(e.author), text(e.year) ? `(${text(e.year)})` : null].filter(Boolean).join(' ');
			return byline || null;
		}],
		['Relevance', (e) => text(e.relevance)],
	],
};

// ── The composite sections — a summary plus one or more titled record lists ────────────

export const ClinicalConsiderations: React.FC<{ value: Record<string, unknown> }> = ({ value }) => (
	<>
		<Html value={value.summary} />
		{hasItems(value.biomarkers) && (
			<>
				<SubHeading>Biomarkers</SubHeading>
				<RecordList items={value.biomarkers as never} spec={BIOMARKER} />
			</>
		)}
		{hasItems(value.screening_recommendations) && (
			<>
				<SubHeading>Screening recommendations</SubHeading>
				<RecordList items={value.screening_recommendations as never} spec={SCREENING} />
			</>
		)}
		{hasItems(value.doctor_discussion_points) && (
			<>
				<SubHeading>Doctor discussion points</SubHeading>
				<BulletList items={value.doctor_discussion_points} />
			</>
		)}
	</>
);

// ── Cognitive-panel sections ──────────────────────────────────────────────────────────

const DIMENSION_ORDER = ['extroversion', 'openness', 'conscientiousness', 'agreeableness', 'neuroticism'] as const;
const titleCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const PersonalityDimensionsSection: React.FC<{ value: AiPanelPersonalityDimensions }> = ({ value }) => {
	const dimensions = value.dimensions ?? {};
	return (
		<>
			<Html value={value.summary} />
			{DIMENSION_ORDER.map((name) => {
				const dimension = dimensions[name];
				if (!dimension || typeof dimension !== 'object') return null;
				return (
					<RecordCard
						key={name}
						title={titleCase(name)}
						extra={<SeverityTag value={text(dimension.assessment)} />}
						rows={[
							{ label: 'Direction', value: text(dimension.direction) },
							{ label: 'Confidence', value: text(dimension.confidence) },
							{ label: 'Genetic influence', value: text(dimension.genetic_influence) },
							{ label: 'Contributing genes', value: tags(dimension.contributing_genes) },
							{ label: 'Recommendations', value: tags(dimension.recommendations) },
						]}
					/>
				);
			})}
		</>
	);
};

export const CognitiveStrengthsSection: React.FC<{ value: AiPanelCognitiveStrengthsOpportunities }> = ({ value }) => (
	<>
		{hasItems(value.innate_strengths) && (
			<>
				<SubHeading>Innate strengths</SubHeading>
				<RecordList
					items={value.innate_strengths as never}
					spec={{
						title: (e: Record<string, unknown>) => text(e.strength),
						fields: [
							['Genetic basis', (e: Record<string, unknown>) => text(e.genetic_basis)],
							['Practical applications', (e: Record<string, unknown>) => e.practical_applications, 'tags'],
						],
					}}
				/>
			</>
		)}
		{hasItems(value.improvement_opportunities) && (
			<>
				<SubHeading>Improvement opportunities</SubHeading>
				<RecordList
					items={value.improvement_opportunities as never}
					spec={{
						title: (e: Record<string, unknown>) => text(e.area),
						fields: [
							['Genetic factors', (e: Record<string, unknown>) => text(e.genetic_factors)],
							['Strategies', (e: Record<string, unknown>) => e.strategies, 'tags'],
							['Expected outcomes', (e: Record<string, unknown>) => text(e.expected_outcomes)],
						],
					}}
				/>
			</>
		)}
	</>
);

// ── Sports-panel sections ─────────────────────────────────────────────────────────────

const percent = (value: unknown): string | null => (text(value) ? `${text(value)}%` : null);

export const PowerVsEnduranceSection: React.FC<{ value: AiPanelPowerVsEndurance }> = ({ value }) => {
	const fibres = value.muscle_fiber_composition ?? {};
	const markers = value.genetic_markers ?? {};
	const suitability = value.sport_suitability ?? {};
	return (
		<>
			<RecordCard
				title={text(value.profile_type) ?? 'Profile'}
				extra={<SeverityTag value={text(value.confidence)} />}
				rows={[
					{ label: 'Primary system', value: text(value.primary_system) },
					{ label: 'Fast twitch', value: percent(fibres.fast_twitch_percentage) },
					{ label: 'Slow twitch', value: percent(fibres.slow_twitch_percentage) },
					{ label: 'Power genes', value: tags(markers.power_genes) },
					{ label: 'Endurance genes', value: tags(markers.endurance_genes) },
					{ label: 'Highly suitable', value: tags(suitability.highly_suitable) },
					{ label: 'Moderately suitable', value: tags(suitability.moderately_suitable) },
					{ label: 'Less suitable', value: tags(suitability.less_suitable) },
				]}
			/>
			<Html value={value.detailed_analysis} />
			{hasItems(value.training_recommendations) && (
				<>
					<SubHeading>Training recommendations</SubHeading>
					<RecordList
						items={value.training_recommendations as never}
						spec={{
							title: (e: Record<string, unknown>) => text(e.type),
							fields: [
								['Frequency', (e: Record<string, unknown>) => text(e.frequency)],
								['Intensity', (e: Record<string, unknown>) => text(e.intensity)],
								['Duration', (e: Record<string, unknown>) => text(e.duration)],
								['Rationale', (e: Record<string, unknown>) => text(e.rationale)],
							],
						}}
					/>
				</>
			)}
		</>
	);
};

export const InjuryRiskSection: React.FC<{ value: AiPanelInjuryRisk }> = ({ value }) => {
	const overall = text(value.overall_risk);
	return (
		<>
			{overall && (
				<div className="mg-overall-risk">
					<span className="mg-overall-risk__label">Overall risk</span>
					<SeverityTag value={overall} />
				</div>
			)}
			<RecordList
				items={value.specific_risks as never}
				spec={{
					title: (e: Record<string, unknown>) => text(e.injury_type),
					tag: (e: Record<string, unknown>) => text(e.risk_level),
					fields: [
						['Contributing genes', (e: Record<string, unknown>) => e.contributing_genes, 'tags'],
						['Prevention strategies', (e: Record<string, unknown>) => e.prevention_strategies, 'tags'],
					],
				}}
			/>
		</>
	);
};

export const RecoveryProfileSection: React.FC<{ value: AiPanelRecoveryProfile }> = ({ value }) => {
	const rest = value.rest_recommendations ?? {};
	return (
		<>
			<RecordCard
				title="Recovery"
				rows={[
					{ label: 'Recovery rate', value: text(value.recovery_rate) },
					{ label: 'Inflammation response', value: text(value.inflammation_response) },
					{ label: 'Contributing genes', value: tags(value.contributing_genes) },
					{ label: 'Between sessions', value: text(rest.between_sessions) },
					{ label: 'Between competitions', value: text(rest.between_competitions) },
					{ label: 'Annual rest periods', value: text(rest.annual_rest_periods) },
				]}
			/>
			{hasItems(value.optimal_recovery_protocols) && (
				<>
					<SubHeading>Recovery protocols</SubHeading>
					<RecordList
						items={value.optimal_recovery_protocols as never}
						spec={{
							title: (e: Record<string, unknown>) => text(e.protocol),
							fields: [
								['Timing', (e: Record<string, unknown>) => text(e.timing)],
								['Duration', (e: Record<string, unknown>) => text(e.duration)],
								['Rationale', (e: Record<string, unknown>) => text(e.rationale)],
							],
						}}
					/>
				</>
			)}
		</>
	);
};

export const PerformanceOptimizationSection: React.FC<{ value: AiPanelPerformanceOptimization }> = ({ value }) => {
	const timing = value.nutrition_timing ?? {};
	return (
		<>
			<RecordCard
				title="Nutrition timing"
				rows={[
					{ label: 'Pre workout', value: text(timing.pre_workout) },
					{ label: 'During workout', value: text(timing.during_workout) },
					{ label: 'Post workout', value: text(timing.post_workout) },
				]}
			/>
			{hasItems(value.supplementation) && (
				<>
					<SubHeading>Supplementation</SubHeading>
					<RecordList
						items={value.supplementation as never}
						spec={{
							title: (e: Record<string, unknown>) => text(e.supplement),
							fields: [
								['Dosage', (e: Record<string, unknown>) => text(e.dosage)],
								['Rationale', (e: Record<string, unknown>) => text(e.rationale)],
							],
						}}
					/>
				</>
			)}
		</>
	);
};
